/**
 * The browser's half of issue #83 — a training session's LIFECYCLE.
 *
 * `status` (`planned` | `completed`) is a third axis on a session,
 * alongside `session_context` (who it is with, #152) and `category` (which
 * skill). A session has two phases and the form used to pretend it had one:
 * you plan it (date, type, location, focus), and afterwards you reflect on
 * it. Asking a player to reflect on a session that has not happened is
 * nonsense, and it is why the form felt heavy.
 *
 * Pure functions over a session or a form state, same as `sessionContext.js`
 * and for the same reason: the rules that decide what the dialog asks for
 * are testable without mounting a dialog. The API enforces all of them
 * independently (`api/src/routes/trainingSessions.ts`) — a form is not an
 * enforcement boundary.
 */

export const PLANNED = "planned";
/** The default, and what every session recorded before #83 means. */
export const COMPLETED = "completed";

/**
 * Today as `YYYY-MM-DD` — the shape `training_date` is stored and compared in.
 * The browser's LOCAL calendar day, not the UTC one (#1093): a player in
 * New Zealand is 12-13 hours ahead of UTC, so the UTC date would call their
 * own today "yesterday" for half of every day.
 */
export function todayIso(now = new Date()) {
  const d = now;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * #1093 — what the client sends so the server decides lifecycle ("is this
 * session still to come?") on the player's calendar day, not UTC's. The
 * server only honours it within one day of its own UTC date
 * (`api/src/lib/trainingStatus.ts` `resolveToday`).
 */
export function clientDateHeaders(now = new Date()) {
  return { "X-Client-Date": todayIso(now) };
}

/**
 * A session is planned unless it says otherwise. Rows written before #83
 * carry no `status` on the wire only if an old cached response is in play;
 * the DB column is NOT NULL, so in practice this default is defensive.
 */
export function statusOf(session) {
  return session?.status === PLANNED ? PLANNED : COMPLETED;
}

export function isPlanned(session) {
  return statusOf(session) === PLANNED;
}

/**
 * THE PROMPT. Prefer the server's `needs_reflection`, computed once per
 * response, so a flag cannot flicker across a reload. Since #1093 the
 * server computes it on the caller's own calendar day (the `X-Client-Date`
 * this client sends, honoured within one day of UTC), so the two clocks
 * agree for every real timezone. The local fallback, on this browser's
 * local day, exists only for a response that predates the field.
 */
export function needsReflection(session, today = todayIso()) {
  if (session && typeof session.needs_reflection === "boolean") return session.needs_reflection;
  return isPlanned(session) && String(session?.training_date || "") < today;
}

/**
 * Does the dialog ask how the session went?
 *
 * One rule covers all three cases, which is why it is one rule: a session
 * dated in the future has not happened, so its reflection fields are HIDDEN
 * (#83 — "hidden, not merely optional"; the API writes them null on a
 * planned create regardless of what any client sends). Today or earlier and
 * they are shown — that covers logging a session after the fact, which is
 * what this log has always been for, AND completing a planned session whose
 * date has passed, which is the same dialog doing the same thing.
 *
 * The `completed` escape hatch is for a row that already HAS a reflection
 * and is being edited: moving its date forward must not hide the text the
 * player wrote, which would read as the form having eaten it.
 */
export function showsReflection(form, editing) {
  if (statusOf(editing) === COMPLETED && editing) return true;
  return String(form?.training_date || "") <= todayIso();
}

/**
 * The completion sentence, shown at the top of the dialog when a planned
 * session's date has passed. Names the thing that changed ("has happened")
 * and what saving will do, because "it flips to completed" is invisible
 * otherwise.
 */
export const COMPLETION_PROMPT =
  "This session has been and gone. Add how it went and it moves into your completed sessions.";

/** Shown under the date when a session is being planned rather than logged. */
export const PLANNED_HINT =
  "This date is in the future, so we'll only ask for the plan. You can add how it went once it's happened.";

/**
 * The list badge. Three states, and `completed` gets none — every session
 * recorded before this ticket is completed by definition, and a badge on
 * all of them would be this feature changing the look of history whose
 * meaning it did not change (the same call #152 made for `casual`).
 */
export function statusBadge(session, today = todayIso()) {
  if (!isPlanned(session)) return null;
  if (needsReflection(session, today)) {
    return { label: "Needs reflection", color: "#D4A017", testid: "needs-reflection" };
  }
  return { label: "Planned", color: "#64748B", testid: "planned" };
}

/**
 * #1281 (D279): does the session carry any reflection text? The same set as
 * the old look's inline `hasReflection` in `SessionCard`: any of the six
 * free-text reflection fields, or a non-empty action item.
 */
export function hasReflectionText(session) {
  if (!session) return false;
  const text = (v) => typeof v === "string" && v.trim() !== "";
  if (
    text(session.lesson_learned) || text(session.what_worked_well) ||
    text(session.areas_for_improvement) || text(session.next_focus_area) ||
    text(session.mindset_reflection) || text(session.overall_reflection)
  ) return true;
  return Array.isArray(session.action_items) && session.action_items.some(text);
}

/**
 * #1281: the status chip on the My training board's session card
 * (TrainingHome board, D243). Three values and null: Needs reflection
 * (navy, emphasised), Planned, Reflected (only with reflection text, D279).
 * A completed session with none gets no status chip. `statusBadge` above is
 * the old look's and stays as it is.
 */
export function boardStatus(session, today = todayIso()) {
  if (needsReflection(session, today)) return { label: "Needs reflection", testid: "needs-reflection", emphasis: true };
  if (isPlanned(session)) return { label: "Planned", testid: "planned" };
  if (hasReflectionText(session)) return { label: "Reflected", testid: "reflected" };
  return null;
}

/**
 * The status a save should ASK for. Only ever `completed`, and only when
 * completing a planned session whose date has passed — there is no client
 * path that asks for `planned`, because the API decides that from the date
 * on create and refuses the reverse transition on update.
 *
 * The API also completes a planned session that arrives carrying real
 * reflection text without this flag, so this is belt to that braces: it
 * makes "I saved the reflection form" mean "complete it" even when the
 * player left every box empty, which is a legitimate way to say "it
 * happened, there is nothing to add".
 */
export function completionPayload(editing, form) {
  if (!editing || !isPlanned(editing)) return {};
  if (String(form?.training_date || "") > todayIso()) return {};
  return { status: COMPLETED };
}

/**
 * The reflection/outcome keys — the same list the API calls
 * `REFLECTION_FIELDS` (`api/src/routes/trainingSessions.ts`), plus the
 * three ratings, which belong here for the client's purposes: the sliders
 * live in the block being hidden, so sending their defaults would be the
 * form claiming to have asked a question it did not render.
 */
export const REFLECTION_FIELDS = [
  "lesson_learned",
  "what_worked_well",
  "areas_for_improvement",
  "next_focus_area",
  "confidence_rating",
  "focus_rating",
  "motivation_rating",
  "mindset_reflection",
  "overall_reflection",
  "action_items",
];

/**
 * Drop the reflection keys from a payload whose reflection block was
 * hidden. The API does not need this — it writes a planned session's
 * reflection fields null whatever the body says — but a form should not
 * post fields it did not show, and on an EDIT this is what stops the
 * dialog's unrendered defaults (7/7/8, `[""]`) from being offered as if
 * the player had chosen them.
 */
export function withoutReflectionFields(payload, showsReflectionBlock) {
  if (showsReflectionBlock) return payload;
  const out = { ...payload };
  for (const key of REFLECTION_FIELDS) delete out[key];
  return out;
}
